import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Trash2, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, downloadAdminFile } from "./api";
import { PageHeader, TextInput, TextArea, Button, Card, EmptyState } from "./ui";
import { formatTRY } from "../lib/modelAnalysis";

const PIPELINE = [
  { v: "all", l: "Tümü" },
  { v: "new", l: "Yeni" },
  { v: "contacted", l: "İletişimde" },
  { v: "approved", l: "Onaylandı" },
  { v: "rejected", l: "Reddedildi" },
  { v: "in_production", l: "Üretimde" },
  { v: "completed", l: "Tamamlandı" },
];

function statusLabel(s) {
  const m = Object.fromEntries(PIPELINE.filter((x) => x.v !== "all").map((x) => [x.v, x.l]));
  return m[s] || s;
}

function StatusBadge({ status }) {
  const tone = {
    new: "border-cyan-500/35 text-cyan-200 bg-cyan-500/10",
    contacted: "border-amber-500/35 text-amber-200 bg-amber-500/10",
    approved: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    rejected: "border-red-500/40 text-red-300 bg-red-500/10",
    in_production: "border-violet-500/40 text-violet-200 bg-violet-500/10",
    completed: "border-zinc-500/40 text-zinc-200 bg-zinc-500/10",
    in_progress: "border-white/15 text-zinc-400 bg-white/5",
    quoted: "border-sky-500/35 text-sky-200 bg-sky-500/10",
    won: "border-emerald-500/30 text-emerald-200/90 bg-emerald-500/5",
    lost: "border-zinc-600 text-zinc-500 bg-zinc-800/30",
  }[status] || "border-white/10 text-zinc-400 bg-white/5";
  return (
    <span
      data-testid={`quote-status-badge-${status}`}
      className={`inline-flex font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border ${tone}`}
    >
      {statusLabel(status)}
    </span>
  );
}

export default function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState({
    status: "new",
    admin_note: "",
    final_price: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/quote-requests", { params: { search, status } });
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  }, [search, status]);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selected) return;
    setDraft({
      status: selected.status || "new",
      admin_note: selected.admin_note ?? "",
      final_price:
        selected.final_price != null && !Number.isNaN(Number(selected.final_price))
          ? String(selected.final_price)
          : "",
    });
  }, [selected]);

  const statusOptions = useMemo(() => {
    const base = PIPELINE.filter((x) => x.v !== "all");
    if (selected && !base.some((x) => x.v === selected.status)) {
      return [...base, { v: selected.status, l: `${selected.status} (eski)` }];
    }
    return base;
  }, [selected]);

  const parseFinalPrice = () => {
    const raw = String(draft.final_price || "").trim().replace(",", ".");
    if (raw === "") return undefined;
    const n = parseFloat(raw);
    return Number.isNaN(n) ? undefined : n;
  };

  const postUpdate = async (body) => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`/admin/quotes/${selected.id}/update`, body);
      toast.success("Kaydedildi");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const saveForm = async () => {
    const fp = parseFinalPrice();
    const body = {
      status: draft.status,
      admin_note: draft.admin_note,
    };
    if (fp !== undefined) body.final_price = fp;
    await postUpdate(body);
  };

  const approveAndSend = async () => {
    const fp = parseFinalPrice();
    await postUpdate({
      status: "approved",
      admin_note: draft.admin_note,
      ...(fp !== undefined ? { final_price: fp } : {}),
    });
  };

  const rejectQuote = async () => {
    await postUpdate({
      status: "rejected",
      admin_note: draft.admin_note,
    });
  };

  const remove = async (id) => {
    if (!window.confirm("Bu teklif kalıcı olarak silinecek. Emin misin?")) return;
    try {
      await api.delete(`/admin/quote-requests/${id}`);
      toast.success("Silindi");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const downloadFile = async (quote) => {
    try {
      await downloadAdminFile(
        `/admin/quote-requests/${quote.id}/file`,
        quote.file_name || quote.file_stored_name || "teklif-dosyasi",
      );
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div>
      <PageHeader title="Teklif Talepleri" description="Lead → sipariş pipeline: durum, nihai fiyat ve not." />
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Ad, e-posta, firma, telefon…"
            data-testid="quotes-search"
            className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 focus:border-[#FF4F00] focus:outline-none font-mono text-sm h-11 pl-10 pr-4"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="quotes-status-filter"
          className="bg-[#050505] border border-white/10 text-white font-mono text-sm h-11 px-3 focus:border-[#FF4F00] focus:outline-none"
        >
          {PIPELINE.map((s) => (
            <option key={s.v} value={s.v}>
              {s.l}
            </option>
          ))}
        </select>
        <Button variant="secondary" onClick={load} data-testid="quotes-search-btn">
          Ara
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState message="Kayıt bulunamadı." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#050505] border-b border-white/10">
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <th className="p-4">Tarih</th>
                  <th className="p-4">İsim</th>
                  <th className="p-4">İletişim</th>
                  <th className="p-4">Malzeme</th>
                  <th className="p-4">Adet</th>
                  <th className="p-4">Dosya</th>
                  <th className="p-4">Durum</th>
                  <th className="p-4 w-24" />
                </tr>
              </thead>
              <tbody data-testid="quotes-table-body">
                {items.map((q) => (
                  <tr key={q.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 font-mono text-[11px] text-zinc-400 whitespace-nowrap">
                      {q.created_at.slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="p-4">
                      <div className="font-display font-bold">{q.name}</div>
                      {q.company && <div className="text-xs text-zinc-500">{q.company}</div>}
                    </td>
                    <td className="p-4 text-xs">
                      <div className="text-zinc-300">{q.email}</div>
                      <div className="text-zinc-500">{q.phone}</div>
                    </td>
                    <td className="p-4 font-mono text-xs">{q.material}</td>
                    <td className="p-4 font-mono text-xs">{q.quantity}</td>
                    <td className="p-4">
                      {q.file_stored_name ? (
                        <button
                          type="button"
                          onClick={() => downloadFile(q)}
                          data-testid={`quote-download-${q.id}`}
                          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00E5FF] hover:text-white"
                        >
                          <Download className="w-3 h-3" /> İndir
                        </button>
                      ) : (
                        <span className="font-mono text-[10px] text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="p-4">
                      <StatusBadge status={q.status} />
                    </td>
                    <td className="p-4 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSelected(q)}
                        data-testid={`quote-view-${q.id}`}
                        className="text-zinc-400 hover:text-[#FF4F00]"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(q.id)}
                        data-testid={`quote-delete-${q.id}`}
                        className="text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 grid place-items-center p-6" onClick={() => !saving && setSelected(null)}>
          <div
            className="max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10 bg-[#0C0C0F] p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="quote-detail-modal"
          >
            <h3 className="font-display text-2xl font-black uppercase tracking-tight mb-2">{selected.name}</h3>
            <div className="mb-6">
              <StatusBadge status={selected.status} />
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-8">
              <Row k="E-posta" v={selected.email} />
              <Row k="Telefon" v={selected.phone} />
              <Row k="Firma" v={selected.company || "—"} />
              <Row k="Malzeme" v={selected.material} />
              <Row k="Adet" v={selected.quantity} />
              <Row k="Termin" v={selected.deadline || "—"} />
              <Row k="Ön fiyat bandı" v={fmtPricingBand(selected.pricing)} />
              <Row k="Nihai fiyat (admin)" v={selected.final_price != null ? formatTRY(selected.final_price) : "—"} />
              <Row k="Tarih" v={selected.created_at.slice(0, 16).replace("T", " ")} />
            </div>

            <div className="space-y-4 mb-8 border-t border-white/10 pt-6">
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Durum</label>
              <select
                value={draft.status}
                onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                data-testid="quote-modal-status"
                className="w-full bg-[#050505] border border-white/10 text-white font-mono text-sm h-10 px-3 focus:border-[#FF4F00] focus:outline-none"
              >
                {statusOptions.map((s) => (
                  <option key={s.v} value={s.v}>
                    {s.l}
                  </option>
                ))}
              </select>
              <TextInput
                label="Nihai fiyat (₺)"
                testid="quote-modal-final-price"
                value={draft.final_price}
                onChange={(e) => setDraft((d) => ({ ...d, final_price: e.target.value }))}
                placeholder="Örn: 1250"
              />
              <TextArea
                label="Admin notu"
                testid="quote-modal-admin-note"
                rows={4}
                value={draft.admin_note}
                onChange={(e) => setDraft((d) => ({ ...d, admin_note: e.target.value }))}
                placeholder="Müşteriye iletilecek veya iç not…"
              />
            </div>

            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4F00] mb-4">Baskı ayarları (müşteri)</div>
            <div className="grid grid-cols-2 gap-4 text-sm mb-8">
              <Row k="Malzeme" v={fmtPS(selected.print_settings?.material ?? selected.material)} />
              <Row k="Doluluk %" v={fmtPS(selected.print_settings?.infill_percent, (n) => `%${n}`)} />
              <Row k="Duvar sayısı" v={fmtPS(selected.print_settings?.wall_count, (n) => String(n))} />
              <Row k="Katman kalınlığı (mm)" v={fmtLayerMm(selected.print_settings?.layer_height_mm)} />
              <Row k="Kalite profili" v={fmtPS(selected.print_settings?.quality_profile)} span />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4F00] mb-4">Analiz & ön fiyat snapshot</div>
            <div className="grid grid-cols-2 gap-4 text-sm mb-8">
              <Row k="pricing_status" v={selected.pricing_status ?? "—"} />
              <Row k="analysis_id" v={selected.analysis_id ?? "—"} />
              <Row
                k="Fiyat ayarları snapshot"
                v={selected.pricing_settings_snapshot?.pricing_engine_version ?? selected.pricing_snapshot?.pricing_engine_version ?? "—"}
              />
              <Row k="Hacim (volume_cm³)" v={fmtVolumeCm3(selected.geometry)} />
              <Row k="confidence" v={selected.pricing?.confidence ?? "—"} />
              <Row k="Tahmini kütle" v={fmtMassRange(selected.mass_estimate_g)} />
              <Row k="Tahmini süre" v={fmtTimeRange(selected.print_time_estimate_h)} />
            </div>

            {selected.notes && (
              <div className="mt-6 mb-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Müşteri notu</div>
                <div className="text-sm text-zinc-300 whitespace-pre-wrap">{selected.notes}</div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 border-t border-white/10 pt-6">
              <Button type="button" disabled={saving} onClick={saveForm} data-testid="quote-modal-save">
                Kaydet
              </Button>
              <Button type="button" variant="secondary" disabled={saving} onClick={approveAndSend} data-testid="quote-modal-approve">
                Fiyatı Onayla ve Gönder
              </Button>
              <Button type="button" variant="danger" disabled={saving} onClick={rejectQuote} data-testid="quote-modal-reject">
                Reddet
              </Button>
              <Button type="button" variant="secondary" className="sm:ml-auto" onClick={() => setSelected(null)} data-testid="quote-modal-close">
                Kapat
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, span = false }) {
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{k}</div>
      <div className="text-zinc-200 break-all">{String(v ?? "—")}</div>
    </div>
  );
}

function fmtPS(val, fmt) {
  if (val === 0 || val === false) return String(val);
  if (val == null || val === "") return "—";
  return fmt ? fmt(val) : String(val);
}

function fmtLayerMm(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `${n.toFixed(2)} mm`;
}

function fmtVolumeCm3(geometry) {
  const v = geometry?.volume_cm3;
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(4)} cm³`;
}

function fmtMassRange(m) {
  if (!m || m.low == null || m.high == null) return "—";
  return `${Number(m.low).toFixed(1)} – ${Number(m.high).toFixed(1)} g`;
}

function fmtTimeRange(h) {
  if (!h || h.low == null || h.high == null) return "—";
  return `${Number(h.low).toFixed(2)} – ${Number(h.high).toFixed(2)} saat`;
}

function fmtPricingBand(pricing) {
  if (!pricing || pricing.low == null || pricing.high == null) return "—";
  return `${formatTRY(pricing.low)} – ${formatTRY(pricing.high)}`;
}
